import { useState, useEffect, useRef } from "react";
import { Link } from "react-router";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { useAdminData } from "./admin/AdminDataContext";
import { useLanguage } from "./LanguageContext";
import { localized } from "./admin/TranslationsPanel";
import { useTheme } from "./ThemeInjector";
import { isSupabaseConnected, fetchWorkshops, fetchWorkshopSessionsWithCounts, type WorkshopSessionWithCount } from "./supabase";
import type { AdminWorkshop } from "./admin/AdminDataContext";

function mapRowToWorkshop(row: {
  id: number | string;
  title: string;
  date: string;
  location: string;
  image?: string | null;
  description?: string | null;
  i18n?: Record<string, unknown> | null;
}): AdminWorkshop {
  return {
    id: Number(row.id),
    title: row.title,
    date: row.date,
    location: row.location,
    image: row.image ?? "",
    description: row.description ?? undefined,
    i18n: (row.i18n ?? {}) as AdminWorkshop["i18n"],
  };
}

const CARD_MIN_WIDTH = 320;

export function WorkshopCarouselSection() {
  const { workshops: contextWorkshops } = useAdminData();
  const { lang, t } = useLanguage();
  const theme = useTheme();
  const scrollRef = useRef<HTMLDivElement>(null);

  const [sessions, setSessions] = useState<WorkshopSessionWithCount[]>([]);
  const [workshops, setWorkshops] = useState<AdminWorkshop[]>(() =>
    isSupabaseConnected() ? [] : contextWorkshops
  );
  const [useSessions, setUseSessions] = useState(false);
  const [loading, setLoading] = useState(isSupabaseConnected());

  useEffect(() => {
    if (!isSupabaseConnected()) {
      setWorkshops(contextWorkshops);
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchWorkshopSessionsWithCounts({ upcomingOnly: true }).then(({ data, error }) => {
      if (!error && data && data.length > 0) {
        setSessions(data);
        setUseSessions(true);
        setLoading(false);
        return;
      }
      fetchWorkshops()
        .then(({ data: wData, error: wErr }) => {
          if (wErr) {
            console.warn("[MOECHA] Failed to fetch workshops:", wErr.message);
            setWorkshops(contextWorkshops);
            return;
          }
          setWorkshops((wData ?? []).map(mapRowToWorkshop));
        })
        .finally(() => setLoading(false));
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isSupabaseConnected()) return;
    setWorkshops(contextWorkshops);
  }, [contextWorkshops]);

  const scroll = (direction: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    const step = el.clientWidth * 0.85;
    const target = el.scrollLeft + (direction === "left" ? -step : step);
    el.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
  };

  const itemsCount = useSessions ? sessions.length : workshops.length;
  if (loading || itemsCount === 0) {
    if (loading) {
      return (
        <section id="workshops" className="py-24" style={{ backgroundColor: `${theme.border}30` }}>
          <div className="max-w-4xl mx-auto px-6 text-center">
            <p style={{ fontFamily: theme.fontBody, fontSize: "14px", fontWeight: 300, color: theme.muted }}>Loading…</p>
          </div>
        </section>
      );
    }
    return null;
  }

  const reserveButtonStyle = {
    fontFamily: theme.fontBody,
    fontSize: "11px",
    fontWeight: 400,
    borderColor: theme.accent,
    color: theme.accent,
    borderRadius: theme.btnRadius,
  };

  return (
    <section id="workshops" className="py-24 overflow-hidden" style={{ backgroundColor: `${theme.border}30` }}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <p
            className="tracking-[0.3em] uppercase mb-2"
            style={{ fontFamily: theme.fontBody, fontSize: "12px", color: theme.accent }}
          >
            {t("workshops.label")}
          </p>
          <h2
            className="tracking-[0.08em]"
            style={{ fontFamily: theme.fontHeading, fontSize: "clamp(26px, 3.5vw, 38px)", fontWeight: 400, color: theme.fg }}
          >
            {t("workshops.title")}
          </h2>
        </div>

        {/* Horizontal scrollable workshop row */}
        <div className="relative">
          <div
            ref={scrollRef}
            className="flex gap-8 overflow-x-auto overflow-y-hidden pb-4 scroll-smooth"
            style={{
              scrollSnapType: "x proximity",
              scrollbarWidth: "thin",
              WebkitOverflowScrolling: "touch",
            }}
            role="list"
          >
            {useSessions
              ? sessions.map((session) => {
                  const title = session.title_override || session.template_title;
                  const location = session.location_override || session.template_location;
                  const maxCap = session.max_capacity ?? 0;
                  const count = Number(session.subscriber_count ?? 0);
                  const isFull = maxCap > 0 && count >= maxCap;
                  return (
                    <div
                      key={session.id}
                      className="shrink-0"
                      style={{
                        minWidth: CARD_MIN_WIDTH,
                        maxWidth: CARD_MIN_WIDTH,
                        scrollSnapAlign: "start",
                      }}
                      role="listitem"
                    >
                      <div className="group bg-white rounded-xl overflow-hidden shadow-[0_8px_30px_rgba(40,32,20,0.06)] hover:shadow-[0_12px_40px_rgba(40,32,20,0.1)] transition-all duration-500 h-full">
                        <div className="overflow-hidden">
                          {session.template_image_url ? (
                            <ImageWithFallback
                              src={session.template_image_url}
                              alt={title ?? ""}
                              className="w-full aspect-[4/3] object-cover group-hover:scale-[1.03] transition-transform duration-700 ease-out"
                            />
                          ) : (
                            <div className="w-full aspect-[4/3] bg-[#F7F5F1]" />
                          )}
                        </div>
                        <div className="p-6">
                          <h3
                            className="tracking-[0.05em] mb-3"
                            style={{ fontFamily: theme.fontHeading, fontSize: "22px", fontWeight: 500, color: theme.fg }}
                          >
                            {title}
                          </h3>
                          <p
                            className="tracking-[0.1em] mb-1"
                            style={{ fontFamily: theme.fontBody, fontSize: "13px", fontWeight: 400, color: theme.accent }}
                          >
                            {new Date(session.starts_at).toLocaleString(undefined, { dateStyle: "long", timeStyle: "short" })}
                          </p>
                          {location && (
                            <p
                              className="tracking-[0.05em] mb-6"
                              style={{ fontFamily: theme.fontBody, fontSize: "13px", fontWeight: 300, color: theme.fg }}
                            >
                              {location}
                            </p>
                          )}
                          {!location && <div className="mb-6" />}
                          <Link
                            to={`/workshops/${session.id}/reserve`}
                            className="block w-full py-3 border text-center tracking-[0.2em] uppercase transition-all duration-300"
                            style={reserveButtonStyle}
                          >
                            {isFull ? (t("workshops.join_waitlist") ?? "Join waitlist") : t("workshops.reserve")}
                          </Link>
                        </div>
                      </div>
                    </div>
                  );
                })
              : workshops.map((w) => (
                  <div
                    key={w.id}
                    className="shrink-0"
                    style={{
                      minWidth: CARD_MIN_WIDTH,
                      maxWidth: CARD_MIN_WIDTH,
                      scrollSnapAlign: "start",
                    }}
                    role="listitem"
                  >
                    <div className="group bg-white rounded-xl overflow-hidden shadow-[0_8px_30px_rgba(40,32,20,0.06)] hover:shadow-[0_12px_40px_rgba(40,32,20,0.1)] transition-all duration-500 h-full">
                      <div className="overflow-hidden">
                        <ImageWithFallback
                          src={w.image}
                          alt={localized(w, "title", lang)}
                          className="w-full aspect-[4/3] object-cover group-hover:scale-[1.03] transition-transform duration-700 ease-out"
                        />
                      </div>
                      <div className="p-6">
                        <h3
                          className="tracking-[0.05em] mb-3"
                          style={{ fontFamily: theme.fontHeading, fontSize: "22px", fontWeight: 500, color: theme.fg }}
                        >
                          {localized(w, "title", lang)}
                        </h3>
                        <p
                          className="tracking-[0.1em] mb-1"
                          style={{ fontFamily: theme.fontBody, fontSize: "13px", fontWeight: 400, color: theme.accent }}
                        >
                          {w.date}
                        </p>
                        <p
                          className="tracking-[0.05em] mb-6"
                          style={{ fontFamily: theme.fontBody, fontSize: "13px", fontWeight: 300, color: theme.fg }}
                        >
                          {localized(w, "location", lang)}
                        </p>
                        <Link
                          to="/workshops"
                          className="block w-full py-3 border text-center tracking-[0.2em] uppercase transition-all duration-300"
                          style={reserveButtonStyle}
                        >
                          {t("workshops.reserve")}
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
          </div>

          {itemsCount > 0 && (
            <div className="flex items-center justify-center gap-4 mt-6">
              <button
                type="button"
                onClick={() => scroll("left")}
                aria-label="Scroll left"
                className="flex items-center justify-center w-12 h-12 rounded-full border transition-all duration-300 hover:scale-[1.02]"
                style={{ borderColor: theme.border, color: theme.fg, backgroundColor: theme.card }}
              >
                <ChevronLeft size={22} strokeWidth={1.5} />
              </button>
              <button
                type="button"
                onClick={() => scroll("right")}
                aria-label="Scroll right"
                className="flex items-center justify-center w-12 h-12 rounded-full border transition-all duration-300 hover:scale-[1.02]"
                style={{ borderColor: theme.border, color: theme.fg, backgroundColor: theme.card }}
              >
                <ChevronRight size={22} strokeWidth={1.5} />
              </button>
            </div>
          )}
        </div>

        <div className="text-center mt-8">
          <Link
            to="/workshops"
            className="inline-block tracking-[0.2em] uppercase transition-opacity hover:opacity-80"
            style={{ fontFamily: theme.fontBody, fontSize: "11px", color: theme.primary }}
          >
            {t("workshops.see_all")}
          </Link>
        </div>
      </div>
    </section>
  );
}
